import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Shield, UserPlus, Edit2, Trash2, Briefcase, X, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../store/authStore';

const emptyForm = { username: '', email: '', full_name: '', password: '', role: 'user' };

export default function Admin() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [err, setErr] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/users');
            setUsers(res.data || []);
        } catch (e) {
            setErr(e.response?.data?.detail || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const remove = async (u) => {
        if (!confirm(`Delete ${u.username}?`)) return; // eslint-disable-line no-alert
        try {
            await api.delete(`/admin/users/${u.id}`);
            load();
        } catch (e) {
            alert(e.response?.data?.detail || 'Failed'); // eslint-disable-line no-alert
        }
    };

    const toggleActive = async (u) => {
        try {
            await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
            load();
        } catch (e) {
            alert(e.response?.data?.detail || 'Failed'); // eslint-disable-line no-alert
        }
    };

    const stats = {
        total: users.length,
        active: users.filter((u) => u.is_active).length,
        admins: users.filter((u) => u.role === 'admin').length,
    };

    return (
        <div className="space-y-6" data-testid="admin-page">
            <header>
                <div className="inline-flex items-center gap-2 rounded-full bg-[#FEF2F2] px-3 py-1 text-xs font-semibold text-[#E52C2C]">
                    <Shield className="h-3.5 w-3.5" /> ADMIN PANEL
                </div>
                <h1 className="mt-2 text-2xl md:text-3xl font-extrabold text-[#111827]">User Management</h1>
                <p className="text-sm text-[#6B7280]">Manage learner and admin accounts.</p>
            </header>

            <div className="grid gap-4 md:grid-cols-4">
                <StatTile label="Total Users" value={stats.total} icon={Users} accent="#1C3A94" />
                <StatTile label="Active" value={stats.active} icon={CheckCircle2} accent="#10B981" />
                <StatTile label="Admins" value={stats.admins} icon={Shield} accent="#E52C2C" />
                <Link to="/admin/jobs" className="brand-card p-5 flex items-center gap-4 hover:border-[#1C3A94]/40 hover:shadow-md transition" data-testid="admin-jobs-link">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#F1F5FD] text-[#1C3A94]">
                        <Briefcase className="h-5 w-5" />
                    </div>
                    <div>
                        <div className="text-xs font-medium text-[#6B7280]">Job Portal</div>
                        <div className="text-sm font-extrabold text-[#111827]">Manage →</div>
                    </div>
                </Link>
            </div>

            <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-[#111827]">All users</h2>
                <button data-testid="add-user-btn" onClick={() => setShowAdd(true)} className="brand-btn-primary gap-2">
                    <UserPlus className="h-4 w-4" /> Add user
                </button>
            </div>

            {err && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>}

            {loading ? (
                <div className="brand-card p-10 flex items-center justify-center gap-3 text-[#6B7280]">
                    <Loader2 className="h-5 w-5 animate-spin" /> Loading…
                </div>
            ) : (
                <div className="brand-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F9FAFB] text-left text-xs uppercase tracking-wide text-[#6B7280]">
                            <tr>
                                <th className="px-4 py-3">User</th>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Role</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} className="border-t border-[#E5E7EB]" data-testid={`user-row-${u.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#1C3A94] to-[#3552B8] text-xs font-bold text-white">
                                                {(u.full_name || u.username)[0].toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="font-semibold text-[#111827]">{u.full_name}</div>
                                                <div className="text-xs text-[#6B7280]">@{u.username}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-[#6B7280]">{u.email}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                            u.role === 'admin' ? 'bg-[#FEF2F2] text-[#E52C2C]' : 'bg-[#F1F5FD] text-[#1C3A94]'
                                        }`}>
                                            {u.role}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            data-testid={`toggle-active-${u.id}`}
                                            onClick={() => toggleActive(u)}
                                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                                u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-[#6B7280]'
                                            }`}
                                        >
                                            {u.is_active ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                            {u.is_active ? 'Active' : 'Inactive'}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end gap-1">
                                            <button data-testid={`edit-user-${u.id}`} onClick={() => setEditUser(u)} className="brand-btn-ghost px-2">
                                                <Edit2 className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                data-testid={`delete-user-${u.id}`}
                                                onClick={() => remove(u)}
                                                disabled={u.id === me?.id}
                                                className="brand-btn-ghost px-2 hover:text-[#E52C2C] disabled:opacity-30"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <AnimatePresence>
                {showAdd && (
                    <UserFormModal
                        initial={emptyForm}
                        title="Add user"
                        onClose={() => setShowAdd(false)}
                        onSubmit={async (payload) => {
                            await api.post('/admin/users', payload);
                            setShowAdd(false);
                            load();
                        }}
                    />
                )}
                {editUser && (
                    <UserFormModal
                        initial={{ ...emptyForm, ...editUser, password: '' }}
                        title="Edit user"
                        isEdit
                        onClose={() => setEditUser(null)}
                        onSubmit={async (payload) => {
                            const p = { ...payload };
                            if (!p.password) delete p.password;
                            delete p.username;
                            await api.put(`/admin/users/${editUser.id}`, p);
                            setEditUser(null);
                            load();
                        }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

function StatTile({ icon: Icon, label, value, accent }) {
    return (
        <div className="brand-card p-5 flex items-center gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg" style={{ background: `${accent}18`, color: accent }}>
                <Icon className="h-5 w-5" />
            </div>
            <div>
                <div className="text-xs font-medium text-[#6B7280]">{label}</div>
                <div className="text-xl font-extrabold text-[#111827]">{value}</div>
            </div>
        </div>
    );
}

function UserFormModal({ initial, title, isEdit, onClose, onSubmit }) {
    const [form, setForm] = useState(initial);
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr('');
        try {
            await onSubmit(form);
        } catch (e2) {
            setErr(e2.response?.data?.detail || 'Failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <motion.form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                className="w-full max-w-lg brand-card p-6"
                data-testid="user-form-modal"
            >
                <div className="flex items-start justify-between gap-4">
                    <h2 className="text-lg font-bold text-[#111827]">{title}</h2>
                    <button type="button" onClick={onClose} className="text-[#9CA3AF] hover:text-[#111827]">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {err && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700">{err}</div>}

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div>
                        <label className="mb-1 block text-xs font-semibold">Username *</label>
                        <input data-testid="uf-username" required disabled={isEdit} className="brand-input" value={form.username} onChange={set('username')} />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold">Full name *</label>
                        <input data-testid="uf-fullname" required className="brand-input" value={form.full_name} onChange={set('full_name')} />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold">Email *</label>
                        <input data-testid="uf-email" required type="email" className="brand-input" value={form.email} onChange={set('email')} />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold">Role *</label>
                        <select data-testid="uf-role" className="brand-input" value={form.role} onChange={set('role')}>
                            <option value="user">User (learner)</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="mb-1 block text-xs font-semibold">
                            Password {isEdit ? '(leave blank to keep current)' : '*'}
                        </label>
                        <input
                            data-testid="uf-password"
                            type="password"
                            className="brand-input"
                            value={form.password || ''}
                            onChange={set('password')}
                            required={!isEdit}
                            minLength={6}
                        />
                    </div>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="brand-btn-outline">Cancel</button>
                    <button type="submit" data-testid="uf-submit" disabled={busy} className="brand-btn-primary">
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </motion.form>
        </motion.div>
    );
}
